import { ErrorCode, type ApiErrorBody, type ApiResponse, type FieldError } from '@aforium/shared';

/**
 * The single HTTP client for the whole app.
 *
 * It owns three responsibilities so no feature has to think about them:
 *  - unwrapping the `{ success, data }` envelope into a plain value,
 *  - turning `{ success: false, error }` into a typed `ApiRequestError`,
 *  - refreshing an expired access token once and replaying the request.
 */

const BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/** Every failed request surfaces as this, so UI error handling has one shape. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: FieldError[];
  readonly meta?: Record<string, unknown>;

  constructor(status: number, error: ApiErrorBody) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = error.code;
    this.details = error.details;
    this.meta = error.meta;
  }

  /** True when the failure is a field-level validation problem. */
  get isValidation(): boolean {
    return this.code === ErrorCode.VALIDATION_ERROR;
  }

  /** Map field errors onto react-hook-form's `setError` shape. */
  get fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const detail of this.details ?? []) {
      result[detail.field] = detail.message;
    }
    return result;
  }
}

/* -------------------------------------------------------------------------- */
/* Access token storage                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The access token lives in memory only. The refresh token is an httpOnly
 * cookie the browser sends automatically, so a XSS payload cannot read either
 * one out of localStorage.
 */
let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Registered by the auth provider so a dead session can clear app state. */
export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

/* -------------------------------------------------------------------------- */
/* Refresh coordination                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Concurrent 401s must not fire N refresh calls: the first one starts the
 * refresh, everyone else awaits the same promise.
 */
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) return null;

      const payload = (await response.json()) as ApiResponse<{ accessToken: string }>;
      if (!payload.success) return null;

      accessToken = payload.data.accessToken;
      return accessToken;
    } catch {
      return null;
    } finally {
      // Release the lock on the next tick so queued callers read the new token.
      setTimeout(() => {
        refreshPromise = null;
      }, 0);
    }
  })();

  return refreshPromise;
}

/* -------------------------------------------------------------------------- */
/* Request                                                                    */
/* -------------------------------------------------------------------------- */

export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Query string parameters; `undefined` and `null` entries are dropped. */
  params?: Record<string, string | number | boolean | undefined | null>;
  /** Set to false for endpoints that must not attempt a token refresh. */
  retryOnUnauthorized?: boolean;
}

function buildUrl(path: string, params?: RequestOptions['params']): string {
  const url = `${BASE_URL}/api${path.startsWith('/') ? path : `/${path}`}`;
  if (!params) return url;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }

  const query = search.toString();
  return query ? `${url}?${query}` : url;
}

async function execute<T>(path: string, options: RequestOptions, isRetry: boolean): Promise<T> {
  const { method = 'GET', body, params, retryOnUnauthorized = true, headers, ...rest } = options;

  const requestHeaders = new Headers(headers);
  if (body !== undefined && !(body instanceof FormData)) {
    requestHeaders.set('Content-Type', 'application/json');
  }
  if (accessToken) {
    requestHeaders.set('Authorization', `Bearer ${accessToken}`);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, params), {
      ...rest,
      method,
      credentials: 'include',
      headers: requestHeaders,
      body:
        body === undefined
          ? undefined
          : body instanceof FormData
            ? body
            : JSON.stringify(body),
    });
  } catch {
    throw new ApiRequestError(0, {
      code: ErrorCode.NETWORK_ERROR,
      message: 'Could not reach the server. Check your connection and try again.',
    });
  }

  // A file download (CSV export) is not enveloped; hand back the blob.
  const contentType = response.headers.get('content-type') ?? '';
  if (response.ok && !contentType.includes('application/json')) {
    return (await response.blob()) as unknown as T;
  }

  let payload: ApiResponse<T> | null = null;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    payload = null;
  }

  if (response.ok && payload?.success) {
    return payload.data;
  }

  const error: ApiErrorBody = payload && !payload.success
    ? payload.error
    : {
        code: response.status === 404 ? ErrorCode.NOT_FOUND : ErrorCode.INTERNAL_ERROR,
        message: 'Something went wrong. Please try again.',
      };

  // One transparent refresh-and-replay for an expired access token.
  if (
    response.status === 401 &&
    retryOnUnauthorized &&
    !isRetry &&
    !path.startsWith('/auth/refresh') &&
    !path.startsWith('/auth/login')
  ) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return execute<T>(path, options, true);
    }
    accessToken = null;
    onUnauthenticated?.();
  }

  throw new ApiRequestError(response.status, error);
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> =>
    execute<T>(path, { ...options, method: 'GET' }, false),

  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> =>
    execute<T>(path, { ...options, method: 'POST', body }, false),

  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> =>
    execute<T>(path, { ...options, method: 'PATCH', body }, false),

  put: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> =>
    execute<T>(path, { ...options, method: 'PUT', body }, false),

  delete: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> =>
    execute<T>(path, { ...options, method: 'DELETE' }, false),

  refresh: refreshAccessToken,
};

/** Trigger a browser download for an export endpoint. */
export async function downloadFile(
  path: string,
  filename: string,
  params?: RequestOptions['params'],
): Promise<void> {
  const blob = await api.get<Blob>(path, { params });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
